import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';

export type ResourceSetFieldSection = 'Details' | 'More fields';

export type ResourceSetFieldRow = {
  key: string;
  label: string;
  value: string;
  section: ResourceSetFieldSection;
};

type Lookups = {
  aorNameById?: ReadonlyMap<string, string>;
  incidentNameById?: ReadonlyMap<string, string>;
};

type Formatter = (value: unknown, set: ResourceSet, lookups: Lookups) => string;

const DATE_KEYS = new Set([
  'date_time_ordered',
  'eta',
  'asset_status_timestamp',
]);

/** Known fields in reading order; anything else lands under "More fields". */
const KNOWN_FIELDS: [key: string, label: string, format?: Formatter][] = [
  ['name', 'Name'],
  ['description', 'Description'],
  ['kind', 'Kind'],
  ['type', 'Type'],
  ['quantity', 'Quantity'],
  ['status', 'Status'],
  ['current_status', 'Current status'],
  ['asset_status', 'Asset status'],
  ['asset_status_timestamp', 'Asset status updated'],
  ['in_request', 'In request'],
  [
    'aor',
    'AOR',
    (value, _set, { aorNameById }) =>
      aorNameById?.get(String(value)) ?? String(value),
  ],
  ['opcon', 'OPCON'],
  ['tacon', 'TACON'],
  ['unit', 'Unit'],
  ['owning_organization', 'Owning organization'],
  ['hull_tail_number', 'Hull / tail number'],
  ['persons_on_board', 'Persons on board'],
  ['point_of_contact', 'Point of contact'],
  ['address', 'Address'],
  ['geometry', 'Coordinates', (value) => formatGeometry(value)],
  ['geolocation', 'Location'],
  ['source_location', 'Source location'],
  ['source_resource_site_id', 'Source site'],
  ['destination_location', 'Destination location'],
  ['destination_resource_site_id', 'Destination site'],
  ['date_time_ordered', 'Ordered'],
  ['eta', 'ETA'],
  [
    'incidents',
    'Incidents',
    (value, set, { incidentNameById }) => {
      const names = new Map<string, string>();
      set.incident_details?.forEach((detail) =>
        names.set(String(detail.id), detail.name)
      );
      return (Array.isArray(value) ? value : [value])
        .map(
          (id) =>
            names.get(String(id)) ??
            incidentNameById?.get(String(id)) ??
            String(id)
        )
        .join(', ');
    },
  ],
  ['notes', 'Notes'],
  ['threats', 'Threats'],
  ['automation_rules', 'Automation rules'],
  ['symbology', 'Symbology'],
  ['consolidation_target_id', 'Consolidation target'],
  ['archived', 'Archived'],
];

/** Shown through another row, or form/UI state rather than stored data. */
const SKIPPED_KEYS = new Set(['incident_details', 'members']);

function humanize(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatGeometry(value: unknown): string {
  const coordinates = (value as { coordinates?: unknown })?.coordinates;
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    const [lon, lat] = coordinates.map(Number);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    }
  }
  return formatValue(value);
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

export function formatValue(value: unknown, key?: string): string {
  if (isEmpty(value)) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key && DATE_KEYS.has(key)) {
    const date = new Date(value as string);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString();
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => formatValue(entry))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const named = record.name ?? record.label ?? record.title;
    if (typeof named === 'string' && named.trim()) return named;
    return Object.entries(record)
      .filter(([, entry]) => !isEmpty(entry))
      .map(
        ([entryKey, entry]) => `${humanize(entryKey)}: ${formatValue(entry)}`
      )
      .join('; ');
  }
  return String(value);
}

/** Every non-empty stored field on a resource set, labeled for reading. */
export function buildResourceSetFieldRows(
  resourceSet: ResourceSet,
  lookups: Lookups = {}
): ResourceSetFieldRow[] {
  const record = resourceSet as unknown as Record<string, unknown>;
  const rows: ResourceSetFieldRow[] = [];
  const seen = new Set<string>(SKIPPED_KEYS);

  KNOWN_FIELDS.forEach(([key, label, format]) => {
    seen.add(key);
    const raw = record[key];
    if (isEmpty(raw)) return;
    const value = format
      ? format(raw, resourceSet, lookups)
      : formatValue(raw, key);
    if (value) rows.push({ key, label, value, section: 'Details' });
  });

  Object.keys(record)
    .filter((key) => !seen.has(key))
    .sort()
    .forEach((key) => {
      const value = formatValue(record[key], key);
      if (value) {
        rows.push({ key, label: humanize(key), value, section: 'More fields' });
      }
    });

  return rows;
}
